import { useCallback } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError, createClient } from '@connectrpc/connect'
import { useTransport } from '@connectrpc/connect-query'
import { PlanSubmissionDownloadFormat } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  DownloadProcurementPlanXmlRequestSchema,
  ProcurementPlanService,
} from '@ebalance/proto/bffuser/v1/procurement_plan_service_pb'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'

/**
 * 需要調達計画 XML / ZIP ダウンロード hook。
 *
 * `ProcurementPlanService.DownloadProcurementPlanXml` を format (XML / ZIP) 付きで呼び、
 * レスポンスの content (Uint8Array) を Blob 経由でブラウザダウンロードする。
 * ファイル名は必ずレスポンスの fileName (OCCTO BP 規格名) を使う。
 *
 * これは query ではなく押下時の命令的呼び出しなので、
 * React Query の useMutation / useQuery は使わず、
 * useTransport + createClient で直接呼ぶ。
 */
export function useDownloadProcurementPlanXml(): {
  download: (
    planSubmissionId: string,
    format: PlanSubmissionDownloadFormat,
  ) => Promise<void>
} {
  const { t } = useTranslation()
  const transport = useTransport()
  const showErrorToast = useErrorToast()

  const download = useCallback(
    async (
      planSubmissionId: string,
      format: PlanSubmissionDownloadFormat,
    ): Promise<void> => {
      const client = createClient(ProcurementPlanService, transport)
      try {
        const res = await client.downloadProcurementPlanXml(
          create(DownloadProcurementPlanXmlRequestSchema, {
            planSubmissionId,
            format,
          }),
        )

        // Blob 経由でブラウザダウンロードを発火する。
        // ファイル名はレスポンスの fileName を使う (frontend で名前を組み立てない)。
        // res.content (Uint8Array) は subarray view の可能性があり、.buffer を直接
        // 渡すと byteOffset/byteLength を無視して余分な byte を取り込み XML が壊れる。
        // 新しい Uint8Array にコピーして view の論理内容 (offset/length) だけを正確に
        // 渡す (= 破損防止 + TS の Uint8Array<ArrayBufferLike> 非互換も回避)。
        const blob = new Blob([new Uint8Array(res.content)], {
          type: res.contentType,
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = res.fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (err) {
        if (err instanceof ConnectError && err.code === Code.NotFound) {
          showErrorToast({
            title: t('page:procurementPlanList.downloadXml.errorNotFound'),
          })
        } else {
          showErrorToast({
            title: t('page:procurementPlanList.downloadXml.errorUnknown'),
          })
        }
      }
    },
    [transport, t, showErrorToast],
  )

  return { download }
}
